import i18n from 'i18next';

import { ConflictFieldConfig } from '@/modules/projects/types/project-conflicts';
import { InternalRateType } from '@/modules/team/types/team-member';

/**
 * Field keys for team member billing conflict resolution
 * Uses form field names as keys for consistency
 */
export type TeamMemberBillingFieldKey =
  | 'internalRateType'
  | 'internalRateCurrency'
  | 'internalRate'
  | 'externalHourlyRate';

/**
 * Field configurations for team member billing conflict resolution
 * Defines how each field should be displayed in the conflicts table
 */
export const teamMemberBillingConflictFields: ConflictFieldConfig<TeamMemberBillingFieldKey>[] = [
  {
    key: 'internalRateType',
    snapshotKey: 'isHourlyRated',
    labelKey: 'conflictFields.billing.rateType',
    // Form uses InternalRateType enum, snapshot uses boolean isHourlyRated
    getFormValueForComparison: (formValue: string | null | undefined) => {
      if (!formValue) return null;
      // Convert to boolean for comparison
      return formValue === InternalRateType.HourlyCostRate ? 'true' : 'false';
    },
    getSnapshotValueForComparison: (snapshotValue: boolean | null | undefined) => {
      if (snapshotValue === null || snapshotValue === undefined) return null;
      return snapshotValue ? 'true' : 'false';
    },
    valueFormatter: (value: boolean | string | null | undefined) => {
      if (value === null || value === undefined) return i18n.t('common:table.noValue');
      // Handle both boolean (from snapshot) and string (from form)
      const isHourly =
        typeof value === 'boolean' ? value : value === InternalRateType.HourlyCostRate;
      return isHourly
        ? i18n.t('authenticated.team:conflictFields.billing.hourlyRate')
        : i18n.t('authenticated.team:conflictFields.billing.monthlyRate');
    },
  },
  {
    key: 'internalRateCurrency',
    snapshotKey: 'currency',
    labelKey: 'conflictFields.billing.currency',
    getFormValueForComparison: (formValue: string | null | undefined) => {
      return formValue || null;
    },
    getSnapshotValueForComparison: (snapshotValue: { code: string } | null) => {
      return snapshotValue?.code || null;
    },
    /**
     * Normalizes any currency value (from snapshot or form) to just the code string
     * Handles both object form { code: string } and string form
     * This ensures only the code is stored in form values, not the full object
     */
    normalizeFormValue: (value: { code?: string } | string | null | undefined): string | null => {
      if (!value) return null;
      if (typeof value === 'string') {
        return value;
      }
      if (typeof value === 'object' && 'code' in value) {
        return value.code || null;
      }
      return null;
    },
    valueFormatter: (value: { code?: string } | string | null) => {
      if (!value) return i18n.t('common:table.noValue');
      if (typeof value === 'object' && value.code) {
        return value.code;
      }
      if (typeof value === 'string') {
        return value;
      }
      return i18n.t('common:table.noValue');
    },
  },
  {
    key: 'internalRate',
    labelKey: 'conflictFields.billing.internalRate',
    valueFormatter: (value: number | null | undefined, context?: { currency?: string }) => {
      if (value === null || value === undefined) return i18n.t('common:table.noValue');
      const currency = context?.currency || '';
      return currency ? `${value} ${currency}` : String(value);
    },
  },
  {
    key: 'externalHourlyRate',
    snapshotKey: 'externalRate',
    labelKey: 'conflictFields.billing.externalRate',
    valueFormatter: (value: number | null | undefined, context?: { currency?: string }) => {
      if (value === null || value === undefined) return i18n.t('common:table.noValue');
      const currency = context?.currency || '';
      return currency ? `${value} ${currency}` : String(value);
    },
  },
];
